/**
 * Cross-links between the three surfaces for the same library subject:
 * Identity Engine (library profile), HEFT profile (interviews + curation), Brand Strategy foundation run.
 */

import { cn } from '@/lib/utils'
import { persuasionLabService } from '@/services/persuasion-lab'
import { BookOpen, Building2, Mic, Package, User, Users } from 'lucide-react'
import { FC, useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { subjectFeaturePath, subjectScope } from '@/modules/subjects/scope-navigation'

export type SubjectStackType = 'person' | 'company' | 'product' | 'group'

export type SubjectStackSurface = 'identity_engine' | 'heft' | 'foundation'

const IDENTITY_SEGMENT: Record<SubjectStackType, string> = {
    person: 'people',
    company: 'companies',
    product: 'products',
    group: 'groups',
}

export function identityEnginePath(subjectType: SubjectStackType, subjectId: string): string {
    return `/dashboard/assets/${IDENTITY_SEGMENT[subjectType]}/${subjectId}`
}

export function heftProfilePath(subjectType: SubjectStackType, subjectId: string): string {
    return `/dashboard/assets/heft/${subjectType}/${subjectId}`
}

export function foundationRunPath(runId: string): string {
    return `/dashboard/brand-strategy/foundations/${runId}`
}

async function resolveFoundationRunId(
    subjectType: SubjectStackType,
    subjectId: string
): Promise<string | null> {
    try {
        const { runs } = await persuasionLabService.listRuns({ rootsOnly: true })
        const match = runs.find(
            r =>
                r.cpp_anchor_type === subjectType &&
                String(r.cpp_anchor_id) === String(subjectId) &&
                !r.parentRunId
        )
        return match?._id ? String(match._id) : null
    } catch {
        return null
    }
}

type LinkSpec = {
    id: SubjectStackSurface
    label: string
    shortLabel: string
    hint: string
    href: string
    icon: typeof User
}

export const SubjectStackCrossLinks: FC<{
    subjectType: SubjectStackType
    subjectId: string
    activeSurface: SubjectStackSurface
    foundationRunId?: string | null
    className?: string
    compact?: boolean
}> = ({ subjectType, subjectId, activeSurface, foundationRunId: foundationRunIdProp, className, compact }) => {
 const location = useLocation()
 const currentScope = location.pathname.startsWith('/dashboard/subjects/') ? subjectScope(subjectType, subjectId) : null
 const [resolvedFoundationRunId, setResolvedFoundationRunId] = useState<string | null>(
        foundationRunIdProp ?? null
    )

    useEffect(() => {
        if (foundationRunIdProp) {
            setResolvedFoundationRunId(foundationRunIdProp)
            return
        }
        let cancelled = false
        void resolveFoundationRunId(subjectType, subjectId).then(id => {
            if (!cancelled) setResolvedFoundationRunId(id)
        })
        return () => {
            cancelled = true
        }
    }, [foundationRunIdProp, subjectType, subjectId])

    const foundationRunId = foundationRunIdProp ?? resolvedFoundationRunId

    const links = useMemo((): LinkSpec[] => {
        const identityIcon =
            subjectType === 'company' ? Building2 : subjectType === 'product' ? Package : subjectType === 'group' ? Users : User
        const items: LinkSpec[] = [
            {
                id: 'identity_engine',
                label: 'Identity Engine',
                shortLabel: 'Identity',
                hint: 'Library profile, message strategy, ALOs',
                href: identityEnginePath(subjectType, subjectId),
                icon: identityIcon,
            },
            {
                id: 'heft',
                label: 'HEFT Profile',
                shortLabel: 'HEFT',
                hint: 'Interviews, identity views, proof curation',
 href: currentScope ? subjectFeaturePath(currentScope, 'heft') : heftProfilePath(subjectType, subjectId),
                icon: Mic,
            },
        ]
        if (foundationRunId) {
            items.push({
                id: 'foundation',
                label: 'Brand Strategy',
                shortLabel: 'Foundation',
                hint: 'Target, TFD, ALO, creative brief',
                href: currentScope
                    ? subjectFeaturePath(currentScope, 'foundations', foundationRunId)
                    : foundationRunPath(foundationRunId),
                icon: BookOpen,
            })
        }
        return items
    }, [currentScope, foundationRunId, subjectId, subjectType])

    return (
        <nav
            aria-label='Related subject views'
            className={cn(
                'flex flex-wrap items-center gap-2',
                compact ? 'text-xs' : 'text-sm',
                className
            )}
        >
            <span className='text-slate-500 mr-0.5 hidden sm:inline'>Same subject:</span>
            {links.map(link => {
                const Icon = link.icon
                const isActive = link.id === activeSurface
                const label = compact ? link.shortLabel : link.label
                return isActive ? (
                    <span
                        key={link.id}
                        title={link.hint}
                        aria-current='page'
                        className={cn(
                            'inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-slate-100 px-2.5 py-1 font-medium text-slate-700',
                            compact && 'px-2 py-0.5'
                        )}
                    >
                        <Icon className='h-3.5 w-3.5 shrink-0 opacity-70' />
                        {label}
                    </span>
                ) : (
                    <Link
                        key={link.id}
                        to={link.href}
                        title={link.hint}
                        className={cn(
                            'inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 font-medium text-slate-700 shadow-sm transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-800',
                            compact && 'px-2 py-0.5'
                        )}
                    >
                        <Icon className='h-3.5 w-3.5 shrink-0 text-slate-500' />
                        {label}
                    </Link>
                )
            })}
        </nav>
    )
}
